/**
 * PPA & Contracts → Invoices.
 *
 * Two jobs (2026 Metis review):
 *   1. Deadline tracker — per NEM week, the Preliminary / Final statement due
 *      dates + Payment date, with a status per leg (sent / paid / due-soon /
 *      overdue) so no weekly invoice or deadline is missed.
 *   2. Verification — upload the issued invoice workbook; the platform parses
 *      it, recomputes the header figures and cross-checks the inputs (buyer's
 *      share, bundled price, LGC price, dates) against the PPA, flagging any
 *      mismatch or bad data.
 *
 * Data: /api/portfolios/<id>/ppa-invoices/{tracker,upload,...}
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Stack, Chip, Button, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, CircularProgress,
  Tooltip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axios from 'axios';
import PpaWorkspaceTabs from './PpaWorkspaceTabs';

const API = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const fmtAud = (v) => (v == null ? '—' : `A$${Number(v).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`);
const fmtDate = (iso) => {
  if (!iso) return '—';
  // Parse as UTC (append Z) so a plain calendar date isn't shifted back a day
  // when rendered with timeZone:'UTC' in a positive-offset browser locale.
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'UTC' });
};

const SEV = {
  pass: { color: '#22c55e', Icon: CheckCircleIcon,   label: 'Pass' },
  warn: { color: '#f59e0b', Icon: WarningAmberIcon,   label: 'Review' },
  fail: { color: '#ef4444', Icon: ErrorOutlineIcon,   label: 'Fail' },
  info: { color: '#64748b', Icon: InfoOutlinedIcon,   label: 'Info' },
};

// Status of one statement leg (preliminary / final) for a week.
function legStatus(leg, dueIso, today) {
  if (leg?.present) {
    const paid = leg.status === 'paid';
    return { text: paid ? 'Paid' : 'Sent', color: paid ? '#22c55e' : '#3b82f6',
             verdict: leg.verdict };
  }
  if (!dueIso) return { text: 'Pending', color: '#64748b' };
  const due = new Date(`${dueIso}T00:00:00`);
  const now = new Date(`${today}T00:00:00`);
  const days = Math.round((due - now) / 86_400_000);
  if (days < 0)  return { text: 'Overdue', color: '#ef4444' };
  if (days <= 7) return { text: `Due in ${days}d`, color: '#f59e0b' };
  return { text: 'Upcoming', color: '#64748b' };
}

function LegCell({ leg, dueIso, today }) {
  const s = legStatus(leg, dueIso, today);
  const vv = leg?.present && leg.verdict ? SEV[leg.verdict] : null;
  return (
    <Stack spacing={0.3}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
        {fmtDate(dueIso)}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Chip label={s.text} size="small"
              sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                    bgcolor: `${s.color}22`, color: s.color }} />
        {vv && (
          <Tooltip title={`Verification: ${vv.label}`} arrow>
            <vv.Icon sx={{ fontSize: 15, color: vv.color }} />
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
}

function VerificationPanel({ result }) {
  if (!result) return null;
  const v = SEV[result.verdict] || SEV.info;
  return (
    <Card variant="outlined" sx={{ mb: 2, borderColor: v.color }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <v.Icon sx={{ color: v.color }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Verification — {v.label.toUpperCase()}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {['pass', 'warn', 'fail', 'info'].map((k) => (
            result.counts?.[k] ? (
              <Chip key={k} label={`${result.counts[k]} ${SEV[k].label}`} size="small"
                    sx={{ bgcolor: `${SEV[k].color}22`, color: SEV[k].color, fontWeight: 700 }} />
            ) : null
          ))}
        </Stack>
        <Table size="small">
          <TableBody>
            {(result.checks || []).map((c, i) => {
              const cv = SEV[c.severity] || SEV.info;
              return (
                <TableRow key={i}>
                  <TableCell sx={{ width: 32, borderBottom: 'none', pr: 0 }}>
                    <cv.Icon sx={{ fontSize: 18, color: cv.color }} />
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{c.label}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.detail}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function SmartestInvoices() {
  const { portfolioId } = useParams();
  const fileRef = useRef(null);

  const [tracker, setTracker] = useState({ today: null, weeks: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [showAllWeeks, setShowAllWeeks] = useState(false);

  const loadTracker = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/portfolios/${portfolioId}/ppa-invoices/tracker`)
      .then((r) => setTracker(r.data || { weeks: [] }))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  useEffect(() => { loadTracker(); }, [loadTracker]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setError(null); setLastResult(null);
    const fd = new FormData();
    fd.append('file', f);
    axios.post(`${API}/api/portfolios/${portfolioId}/ppa-invoices/upload`, fd)
      .then((r) => { setLastResult(r.data); loadTracker(); })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => { setUploading(false); if (fileRef.current) fileRef.current.value = ''; });
  };

  const weeks = tracker.weeks || [];
  const today = tracker.today;
  // Default view: weeks around "now" (anything not-yet-sent + recent). Toggle
  // reveals the whole year.
  const relevant = showAllWeeks ? weeks : weeks.filter((w) => {
    const anyOpen = !w.preliminary?.present || !w.final?.present;
    const near = w.payment_date && today &&
      Math.abs((new Date(w.payment_date) - new Date(today)) / 86_400_000) <= 45;
    return anyOpen && (near || !w.final?.present);
  });
  const shown = relevant.length ? relevant : weeks;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <PpaWorkspaceTabs />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }}>Invoices</Typography>
        <Button
          variant="contained" startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          onClick={() => fileRef.current?.click()} disabled={uploading}
        >
          {uploading ? 'Verifying…' : 'Upload & verify invoice'}
        </Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm" hidden onChange={onFile} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Weekly preliminary / final statement deadlines to the offtaker, and
        input + calculation checks on each issued invoice.
      </Typography>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {lastResult && (
        <>
          {lastResult.face && (
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Verified: {lastResult.face.statement_type ? `${lastResult.face.statement_type} · ` : ''}
              Week {lastResult.face.aemo_week} {lastResult.face.year} · total {fmtAud(lastResult.face.total_aud)}
              {lastResult.calendar_weeks_updated ? ` · calendar refreshed (${lastResult.calendar_weeks_updated} weeks)` : ''}
            </Typography>
          )}
          <VerificationPanel result={lastResult.verification} />
        </>
      )}

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Weekly billing deadline tracker
            </Typography>
            <Button size="small" onClick={() => setShowAllWeeks((s) => !s)}
                    endIcon={showAllWeeks ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
              {showAllWeeks ? 'Show current' : `Show all ${weeks.length} weeks`}
            </Button>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={26} /></Box>
          ) : !weeks.length ? (
            <Alert severity="info">
              No billing calendar yet. Upload a weekly invoice workbook — its
              <code> aemo_dates </code> sheet seeds the deadline calendar.
            </Alert>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Week</TableCell>
                    <TableCell>Billing period</TableCell>
                    <TableCell>Preliminary</TableCell>
                    <TableCell>Final</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((w) => (
                    <TableRow key={`${w.year}-${w.week}`} hover>
                      <TableCell sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {w.week}<Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}> ·{String(w.year).slice(2)}</Box>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {fmtDate(w.billing_start)} – {fmtDate(w.billing_end)}
                      </TableCell>
                      <TableCell><LegCell leg={w.preliminary} dueIso={w.prelim_date} today={today} /></TableCell>
                      <TableCell><LegCell leg={w.final} dueIso={w.final_date} today={today} /></TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtDate(w.payment_date)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {fmtAud(w.final?.total_aud ?? w.preliminary?.total_aud)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
